import { motion } from 'framer-motion';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import CTASection from '../components/ui/CTASection';
import PageHero from '../components/ui/PageHero';
import IndustriesGrid from '../components/ui/IndustriesGrid';
import { industries } from '../data/content';

const industryDetails = [
  {
    name: 'Startups',
    icon: '🚀',
    description: 'We help startups navigate the complex regulatory landscape from day one — company registration, Startup India recognition, GST setup, and ongoing compliance.',
    services: ['Company Registration', 'Startup India Registration', 'GST Registration', 'Trademark Registration', 'Legal Agreements'],
  },
  {
    name: 'SMEs',
    icon: '🏢',
    description: 'Small and Medium Enterprises trust us for complete compliance management, financial advisory, and business scaling support.',
    services: ['GST & Income Tax Filing', 'ROC Compliance', 'MSME Registration', 'Accounting Outsourcing', 'ISO Certification'],
  },
  {
    name: 'Enterprises',
    icon: '🏛️',
    description: 'Large corporations rely on our specialized teams for complex compliance, audit, legal documentation, and regulatory advisory.',
    services: ['Statutory Audit', 'Company Compliance', 'MCA Filing', 'Legal Consultation', 'Corporate Agreements'],
  },
  {
    name: 'E-Commerce',
    icon: '🛒',
    description: 'E-commerce businesses need specialized compliance for GST, import/export, payment gateways, and marketplace registrations.',
    services: ['GST Registration', 'IEC Code', 'GeM Registration', 'FSSAI Registration', 'E-Commerce Website'],
  },
  {
    name: 'Manufacturing',
    icon: '🏭',
    description: 'Manufacturing companies need industry-specific compliance for GST, factory registration, import/export, and environmental certifications.',
    services: ['GST Compliance', 'IEC Registration', 'ISO Certification', 'MSME Registration', 'Audit Services'],
  },
  {
    name: 'Healthcare',
    icon: '🏥',
    description: 'Healthcare entities require specialized compliance for clinical establishments, FSSAI, GST, and legal documentation.',
    services: ['Company Registration', 'GST Services', 'Legal Agreements', 'Compliance Advisory', 'Accounting Services'],
  },
  {
    name: 'NGOs & Trusts',
    icon: '🤝',
    description: 'Non-profit organizations need specialized registration, 80G/12A exemptions, FCRA compliance, and annual filings.',
    services: ['NGO Registration', 'Trust Registration', 'NGO Compliance', 'NGO Audit', 'Annual Filing'],
  },
  {
    name: 'Education',
    icon: '🎓',
    description: 'Educational institutions need proper registration, trust/society formation, GST compliance, and legal documentation.',
    services: ['Society Registration', 'Trust Registration', 'GST Services', 'Compliance Advisory', 'Legal Documentation'],
  },
];

export default function IndustriesPage() {
  return (
    <main className="w-full overflow-x-hidden pt-16 lg:pt-20">
      {/* Hero */}
      <PageHero
        badge="Industries We Serve"
        title="Serving Businesses Across All Sectors"
        subtitle="Our specialized expertise spans diverse industries, delivering tailored solutions that address sector-specific compliance, legal, and technology needs."
      />

      {/* Quick Grid */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading badge="Our Reach" title="Industries We Specialize In" />
          <IndustriesGrid industries={industries} className="mb-12" />
        </Container>
      </section>

      {/* Detailed Industry Cards */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading badge="Sector Expertise" title="Tailored Solutions for Every Industry" subtitle="We understand the unique compliance and operational needs of each industry we serve." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industryDetails.map((ind, i) => (
              <motion.div
                key={ind.name}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card p-7"
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-4xl">{ind.icon}</span>
                  <h3 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>{ind.name}</h3>
                </div>
                <p className="text-sm mb-5" style={{ color: 'var(--color-text-muted)' }}>{ind.description}</p>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-secondary)' }}>Key Services</p>
                  <div className="flex flex-wrap gap-2">
                    {ind.services.map(s => (
                      <span key={s} className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--color-surface-alt)', color: 'var(--color-primary)', border: '1px solid var(--color-border)' }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <CTASection title="Serving Your Industry?" subtitle="Talk to our industry specialists for a free, no-obligation consultation." />
    </main>
  );
}
